(() => {
  const KEY = '__capRequestCircuitV1';
  if (window[KEY]) return;

  const TRANSIENT_STATUS = new Set([
    408, 425, 429,
    500, 502, 503, 504, 507, 508, 509, 520, 521, 522, 523, 524
  ]);
  const FAILURE_WINDOW_MS = 15000;
  const INITIAL_COOLDOWN_MS = 8000;
  const MAX_COOLDOWN_MS = 60000;
  const state = { consecutiveFailures: 0, lastFailureAt: 0, openedUntil: 0, cooldownMs: INITIAL_COOLDOWN_MS };
  const now = () => Date.now();

  function retryAfterMs(response) {
    const raw = response?.headers?.get?.('Retry-After');
    if (!raw) return 0;
    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
    const timestamp = Date.parse(raw);
    return Number.isFinite(timestamp) ? Math.max(0, timestamp - now()) : 0;
  }

  function isTransient(value) {
    if (!value) return true;
    if (value.name === 'AbortError' || value.name === 'CapCircuitOpen') return false;
    const status = Number(value.status || value.response?.status || 0);
    if (status) return TRANSIENT_STATUS.has(status);
    if (value instanceof Response) return !value.ok;
    return true;
  }

  function resetAfterSuccess() {
    state.consecutiveFailures = 0;
    state.lastFailureAt = 0;
    state.openedUntil = 0;
    state.cooldownMs = INITIAL_COOLDOWN_MS;
  }

  function recordFailure(value) {
    if (!isTransient(value)) return;
    const timestamp = now();
    if (timestamp - state.lastFailureAt > FAILURE_WINDOW_MS) state.consecutiveFailures = 0;
    state.lastFailureAt = timestamp;
    state.consecutiveFailures += 1;
    if (state.consecutiveFailures < 3) return;
    const cooldown = Math.max(state.cooldownMs, retryAfterMs(value));
    state.openedUntil = timestamp + cooldown;
    state.cooldownMs = Math.min(MAX_COOLDOWN_MS, Math.max(INITIAL_COOLDOWN_MS, cooldown * 2));
  }

  function beforeRequest(signal) {
    if (signal?.aborted) throw new DOMException('The request was aborted.', 'AbortError');
    const remaining = state.openedUntil - now();
    if (remaining <= 0) return;
    const error = new Error('Search requests are temporarily paused after repeated server failures.');
    error.name = 'CapCircuitOpen';
    error.retryAfterMs = remaining;
    throw error;
  }

  async function run(task, signal) {
    beforeRequest(signal);
    try {
      const response = await task();
      if (response && response.ok === false) recordFailure(response);
      else resetAfterSuccess();
      return response;
    } catch (error) {
      if (error?.name !== 'AbortError' && error?.name !== 'CapCircuitOpen') recordFailure(error);
      throw error;
    }
  }

  window[KEY] = Object.freeze({
    run,
    isOpen: () => state.openedUntil > now(),
    snapshot: () => ({
      consecutiveFailures: state.consecutiveFailures,
      openedUntil: state.openedUntil,
      cooldownMs: state.cooldownMs
    })
  });
})();

/* ISSUE73_CANARY_PARTIAL_PROFILE_SAVE_OVERLAY_V2 */
(function issue73PartialProfileCanary(window, document) {
  'use strict';

  var GUARD = '__CAP_ISSUE73_PROFILE_CANARY_V2__';
  if (window[GUARD]) return;
  window[GUARD] = true;

  var PROFILE_FIELDS = ['first_name', 'last_name', 'phone', 'email'];
  var ADDRESS_KEYS = ['company', 'address1', 'address2', 'city', 'province_code', 'zip', 'country_code'];
  var saveInFlight = false;

  function byId(id) { return document.getElementById(id); }
  function clean(value) { return String(value == null ? '' : value).trim(); }
  function lower(value) { return clean(value).toLowerCase(); }
  function digits(value) { return clean(value).replace(/\D/g, ''); }
  function fieldValue(id) { var node = byId(id); return node ? clean(node.value) : ''; }
  function hasOwn(object, key) { return Object.prototype.hasOwnProperty.call(object || {}, key); }

  function canonicalPhone(value) {
    var raw = digits(value);
    if (raw.length === 11 && raw.charAt(0) === '1') raw = raw.slice(1);
    return raw.length === 10 ? raw : '';
  }

  function requestId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var bytes = new Uint8Array(16);
    if (!window.crypto || typeof window.crypto.getRandomValues !== 'function') {
      throw new Error('A secure request ID could not be generated. Refresh the page and try again.');
    }
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    var hex = Array.prototype.map.call(bytes, function(part) { return part.toString(16).padStart(2, '0'); }).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  function setProfileStatus(message, tone) {
    var node = byId('rep-customer-profile-status');
    if (!node) return;
    node.textContent = message || '';
    node.className = 'rep-profile-status' + (tone ? ' rep-profile-status--' + tone : '');
  }

  function normalizeAddress(raw) {
    raw = raw && typeof raw === 'object' ? raw : {};
    return {
      company: clean(raw.company),
      address1: clean(raw.address1),
      address2: clean(raw.address2),
      city: clean(raw.city),
      province_code: clean(raw.province_code || raw.provinceCode).toUpperCase(),
      zip: clean(raw.zip),
      country_code: clean(raw.country_code || raw.countryCode).toUpperCase()
    };
  }

  function addressFromForm() {
    return normalizeAddress({
      company: fieldValue('rep-edit-address-company'),
      address1: fieldValue('rep-edit-address1'),
      address2: fieldValue('rep-edit-address2'),
      city: fieldValue('rep-edit-city'),
      province_code: fieldValue('rep-edit-province-code'),
      zip: fieldValue('rep-edit-postal-code'),
      country_code: fieldValue('rep-edit-country-code')
    });
  }

  function addressDiffers(left, right) {
    return ADDRESS_KEYS.some(function(key) { return lower(left[key]) !== lower(right[key]); });
  }

  function addressComplete(address) {
    return Boolean(
      address.address1 &&
      address.city &&
      address.province_code &&
      address.zip &&
      /^[A-Z]{2}$/.test(address.country_code)
    );
  }

  function parseProfile(payload) {
    payload = payload && typeof payload === 'object' ? payload : {};
    var raw = payload.profile || payload.customer || payload.data || payload.result || null;
    if (raw && raw.profile && typeof raw.profile === 'object') raw = raw.profile;
    if (raw && raw.customer && typeof raw.customer === 'object' && !raw.customer_id && !raw.id) raw = raw.customer;
    if (!raw || typeof raw !== 'object') return null;
    return {
      customer_id: clean(raw.customer_id || raw.id),
      first_name: clean(raw.first_name || raw.firstName),
      last_name: clean(raw.last_name || raw.lastName),
      phone: clean(raw.phone || (raw.defaultPhoneNumber && raw.defaultPhoneNumber.phoneNumber)),
      email: clean(raw.email || (raw.defaultEmailAddress && raw.defaultEmailAddress.emailAddress)),
      default_address: normalizeAddress(raw.default_address || raw.defaultAddress)
    };
  }

  function looksLikePhoneFallback(firstName, lastName, current) {
    if (clean(current.first_name) || clean(current.last_name)) return false;
    var combined = clean(firstName + ' ' + lastName);
    return /^[+\d\s().-]+$/.test(combined) && digits(combined).length >= 7;
  }

  async function parseResponse(response) {
    var text = await response.text();
    var data = {};
    if (text) {
      try { data = JSON.parse(text); }
      catch (error) { throw new Error('Customer profile service returned an invalid response. Your edits were kept.'); }
    }
    if (!response.ok || !data || data.ok !== true) {
      throw new Error(clean(data && (data.message || data.code)) || ('Customer profile request failed (HTTP ' + response.status + '). Your edits were kept.'));
    }
    return data;
  }

  async function profileRequest(action, payload) {
    var gate = window.CAP_SR1;
    if (!gate || typeof gate.customerProfileFetch !== 'function') {
      throw new Error('Secure Sales Rep access is not ready. Refresh the page and sign in again. Your edits were kept.');
    }
    await Promise.resolve(gate.ready);
    var body = Object.assign({
      request_id: requestId(),
      source: 'issue73_partial_profile_canary_v2',
      requested_at: new Date().toISOString()
    }, payload || {});
    var response = await gate.customerProfileFetch(action, body);
    return parseResponse(response);
  }

  async function authoritativeProfile(customerId) {
    var data = await profileRequest('get_customer_profile', {
      customer_id: customerId,
      recent_orders_limit: 3
    });
    var profile = parseProfile(data);
    if (!profile || profile.customer_id !== customerId) {
      throw new Error('Shopify profile readback did not match the selected customer. Your edits were kept.');
    }
    return profile;
  }

  function requestedFields(patch) {
    return PROFILE_FIELDS.filter(function(key) { return hasOwn(patch, key); });
  }

  function assertReadback(patch, profile) {
    var mismatches = [];
    if (hasOwn(patch, 'first_name') && clean(profile.first_name) !== clean(patch.first_name)) mismatches.push('first name');
    if (hasOwn(patch, 'last_name') && clean(profile.last_name) !== clean(patch.last_name)) mismatches.push('last name');
    if (hasOwn(patch, 'phone') && canonicalPhone(profile.phone) !== canonicalPhone(patch.phone)) mismatches.push('phone');
    if (hasOwn(patch, 'email') && lower(profile.email) !== lower(patch.email)) mismatches.push('email');
    if (hasOwn(patch, 'default_address') && addressDiffers(normalizeAddress(profile.default_address), normalizeAddress(patch.default_address))) {
      mismatches.push('default address');
    }
    if (mismatches.length) {
      throw new Error('Shopify did not confirm the saved ' + mismatches.join(', ') + '. Your edits were kept.');
    }
  }

  function setInput(id, value) {
    var node = byId(id);
    if (node) node.value = value == null ? '' : value;
  }

  function hydrateConfirmed(profile, patch, phoneFallbackDetected) {
    if (hasOwn(patch, 'first_name') || phoneFallbackDetected) {
      setInput('rep-edit-first-name', profile.first_name);
      setInput('cart-customer-first-name', profile.first_name);
    }
    if (hasOwn(patch, 'last_name') || phoneFallbackDetected) {
      setInput('rep-edit-last-name', profile.last_name);
      setInput('cart-customer-last-name', profile.last_name);
    }
    if (hasOwn(patch, 'phone')) {
      setInput('rep-edit-phone', profile.phone);
      setInput('cart-customer-phone', profile.phone);
    }
    if (hasOwn(patch, 'email')) {
      setInput('rep-edit-email', profile.email);
      setInput('cart-customer-email', profile.email);
    }
    if (hasOwn(patch, 'default_address')) {
      var address = normalizeAddress(profile.default_address);
      setInput('rep-edit-address-company', address.company);
      setInput('rep-edit-address1', address.address1);
      setInput('rep-edit-address2', address.address2);
      setInput('rep-edit-city', address.city);
      setInput('rep-edit-province-code', address.province_code);
      setInput('rep-edit-postal-code', address.zip);
      setInput('rep-edit-country-code', address.country_code);
    }
  }

  async function savePartialProfile(button) {
    if (saveInFlight) return;

    var customerId = fieldValue('cart-shopify-customer-id');
    if (!/^gid:\/\/shopify\/Customer\/[1-9]\d*$/.test(customerId)) {
      setProfileStatus('Select a Shopify customer before saving customer details.', 'error');
      return;
    }

    var typed = {
      first_name: fieldValue('rep-edit-first-name'),
      last_name: fieldValue('rep-edit-last-name'),
      phone: fieldValue('rep-edit-phone'),
      email: fieldValue('rep-edit-email'),
      default_address: addressFromForm()
    };
    var wasDisabled = button.disabled;
    saveInFlight = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setProfileStatus('Checking current Shopify customer details...', 'loading');

    try {
      var current = await authoritativeProfile(customerId);
      var phoneFallbackDetected = looksLikePhoneFallback(typed.first_name, typed.last_name, current);
      var effectiveFirstName = phoneFallbackDetected ? '' : typed.first_name;
      var effectiveLastName = phoneFallbackDetected ? '' : typed.last_name;
      var patch = {
        request_id: requestId(),
        source: 'issue73_partial_profile_canary_v2',
        requested_at: new Date().toISOString(),
        customer_id: customerId
      };

      if (effectiveFirstName !== clean(current.first_name)) {
        if (!effectiveFirstName || effectiveFirstName.length > 80) {
          throw new Error('First name cannot be blank and must be 80 characters or fewer. Your edits were kept.');
        }
        patch.first_name = effectiveFirstName;
      }
      if (effectiveLastName !== clean(current.last_name)) {
        if (effectiveLastName.length > 80) throw new Error('Last name must be 80 characters or fewer. Your edits were kept.');
        patch.last_name = effectiveLastName;
      }
      if (canonicalPhone(typed.phone) !== canonicalPhone(current.phone)) {
        if (!canonicalPhone(typed.phone)) throw new Error('Enter a valid 10-digit phone number before changing the phone. Your edits were kept.');
        patch.phone = typed.phone;
      }
      if (lower(typed.email) !== lower(current.email)) {
        if (!typed.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed.email)) {
          throw new Error('Enter a valid email before changing the email. Clearing email is not supported here. Your edits were kept.');
        }
        patch.email = typed.email;
      }

      var currentAddress = normalizeAddress(current.default_address);
      var addressChanged = addressDiffers(typed.default_address, currentAddress);
      var incompleteAddressPreserved = false;
      if (addressChanged) {
        if (addressComplete(typed.default_address)) {
          patch.default_address = typed.default_address;
        } else {
          incompleteAddressPreserved = true;
        }
      }

      var profileKeys = requestedFields(patch);
      var hasProfileChange = profileKeys.length > 0;
      var hasAddressChange = hasOwn(patch, 'default_address');

      if (!hasProfileChange && !hasAddressChange) {
        if (incompleteAddressPreserved) {
          throw new Error('Complete the address before saving an address change. You can leave address fields blank and edit the other customer details without an address.');
        }
        if (phoneFallbackDetected) {
          setInput('rep-edit-first-name', '');
          setInput('rep-edit-last-name', '');
          setInput('cart-customer-first-name', '');
          setInput('cart-customer-last-name', '');
        }
        setProfileStatus('No customer detail changes to save.', 'success');
        return;
      }

      setProfileStatus('Saving customer details...', 'loading');
      var updateData = await profileRequest('update_customer_profile', patch);
      var updateProfile = parseProfile(updateData);
      if (updateProfile && updateProfile.customer_id === customerId) assertReadback(patch, updateProfile);

      var confirmed = await authoritativeProfile(customerId);
      assertReadback(patch, confirmed);
      hydrateConfirmed(confirmed, patch, phoneFallbackDetected);

      if (incompleteAddressPreserved && !hasAddressChange) {
        setProfileStatus('Customer details saved and verified. The incomplete address was not saved; its typed values were kept.', 'success');
      } else {
        setProfileStatus('Customer details saved and verified in Shopify.', 'success');
      }
    } catch (error) {
      setProfileStatus(clean(error && error.message) || 'Customer details could not be saved. Your edits were kept.', 'error');
    } finally {
      saveInFlight = false;
      button.disabled = wasDisabled;
      button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', function(event) {
    var target = event.target && event.target.closest ? event.target.closest('#rep-save-customer-profile') : null;
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    savePartialProfile(target);
  }, true);
})(window, document);
